import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  History, 
  Sparkles, 
  ChevronRight, 
  Calendar, 
  Filter,
  Footprints,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import FloatingNav from '../components/FloatingNav';

const HistoryPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const months = [
    { value: 'all', label: 'Todos os meses' },
    { value: '1', label: 'Janeiro' },
    { value: '2', label: 'Fevereiro' },
    { value: '3', label: 'Março' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Maio' },
    { value: '6', label: 'Junho' },
    { value: '7', label: 'Julho' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => (currentYear - i).toString());

  useEffect(() => {
    fetchReadings();
  }, [selectedMonth, selectedYear]);

  const fetchReadings = async () => {
    setLoading(true);
    try {
      let url = '/readings';
      if (selectedMonth !== 'all') {
        url += `?month=${selectedMonth}&year=${selectedYear}`;
      }
      const response = await api.get(url);
      setReadings(response.data.readings);
    } catch (error) {
      console.error('Error fetching readings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReactivityColor = (score) => {
    if (score >= 0.7) return 'text-red-500';
    if (score >= 0.4) return 'text-amber-500';
    return 'text-green-500';
  };

  const getReactivityBg = (score) => {
    if (score >= 0.7) return 'bg-red-50';
    if (score >= 0.4) return 'bg-amber-50';
    return 'bg-green-50';
  };

  return (
    <div className="min-h-screen pb-28" style={{ background: 'linear-gradient(180deg, #FDF9F3 0%, #F8F5EF 100%)' }}>
      {/* Header */}
      <header
        className="px-4 py-5 flex items-center gap-4 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at top right, #0F766E 0%, #083D3A 60%, #0A0F14 100%)',
        }}
      >
        <div className="absolute inset-0 scanner-grid opacity-25 pointer-events-none" />
        <button 
          onClick={() => navigate(-1)}
          className="relative z-10 p-2 text-white/80 hover:text-cyan-300 transition-colors rounded-xl hover:bg-white/5"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="relative z-10">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-cyan-300">Registros</p>
          <h1 className="font-bold text-xl text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
            Histórico de Leituras
          </h1>
        </div>
      </header>

      <main className="px-4 py-6 max-w-lg mx-auto">
        {/* Filters */}
        <section className="mb-6 animate-fade-in-up">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#E8E4D9]/50">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#2E5C55]/10 flex items-center justify-center">
                <Filter className="h-5 w-5 text-[#2E5C55]" />
              </div>
              <span className="font-medium text-[#1C1C1E]">Filtrar por período</span>
            </div>
            <div className="flex gap-3">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="flex-1 h-12 rounded-xl border-2 border-[#E8E4D9] bg-[#FDFBF7]" data-testid="month-filter">
                  <SelectValue placeholder="Mês" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-28 h-12 rounded-xl border-2 border-[#E8E4D9] bg-[#FDFBF7]" data-testid="year-filter">
                  <SelectValue placeholder="Ano" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Stats Summary */}
        {readings.length > 0 && (
          <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#E8E4D9]/50 text-center">
                <div className="w-10 h-10 rounded-xl bg-[#2E5C55]/10 flex items-center justify-center mx-auto mb-2">
                  <History className="h-5 w-5 text-[#2E5C55]" />
                </div>
                <p className="text-2xl font-bold text-[#2E5C55]">{readings.length}</p>
                <p className="text-sm text-[#8E8E93]">Leituras</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#E8E4D9]/50 text-center">
                <div className="w-10 h-10 rounded-xl bg-[#D4A373]/10 flex items-center justify-center mx-auto mb-2">
                  <TrendingUp className="h-5 w-5 text-[#D4A373]" />
                </div>
                <p className="text-2xl font-bold text-[#D4A373]">
                  {Math.round(readings.reduce((acc, r) => acc + r.reactivity_score, 0) / readings.length * 100)}%
                </p>
                <p className="text-sm text-[#8E8E93]">Média reativ.</p>
              </div>
            </div>
          </section>
        )}

        {/* Readings List */}
        <section className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-4 border border-[#E8E4D9]/50 animate-pulse">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#E8E4D9]" />
                    <div className="flex-1">
                      <div className="h-4 bg-[#E8E4D9] rounded w-3/4 mb-2" />
                      <div className="h-3 bg-[#E8E4D9] rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : readings.length > 0 ? (
            <div className="space-y-3">
              {readings.map((reading, index) => (
                <Link 
                  key={reading.id} 
                  to={`/leitura/${reading.id}`}
                  className="bg-white rounded-2xl p-4 flex items-center gap-4 border border-[#E8E4D9]/50 hover:shadow-lg hover:-translate-y-0.5 transition-all block animate-fade-in-up"
                  style={{ animationDelay: `${(index + 3) * 0.05}s` }}
                  data-testid={`reading-${reading.id}`}
                >
                  <div className="w-14 h-14 rounded-xl gradient-primary flex items-center justify-center">
                    <Footprints className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#1C1C1E] mb-1">
                      Leitura #{reading.id.slice(0, 8)}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-[#8E8E93] mb-2">
                      <Calendar className="h-4 w-4" />
                      {formatDate(reading.created_at)}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs px-2 py-1 rounded-full bg-[#E8E4D9] text-[#48484A]">
                        {reading.zones?.length || 0} zonas
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getReactivityBg(reading.reactivity_score)} ${getReactivityColor(reading.reactivity_score)}`}>
                        {Math.round(reading.reactivity_score * 100)}% reativo
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-[#8E8E93]" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-8 text-center border border-[#E8E4D9]/50 shadow-lg">
              <div className="w-20 h-20 rounded-full bg-[#E8E4D9] flex items-center justify-center mx-auto mb-4">
                <History className="h-10 w-10 text-[#8E8E93]" />
              </div>
              <h3 className="font-bold text-xl text-[#1C1C1E] mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                Nenhuma leitura encontrada
              </h3>
              <p className="text-[#8E8E93] mb-6">
                {selectedMonth !== 'all' 
                  ? 'Não há leituras para o período selecionado.' 
                  : 'Você ainda não fez nenhuma leitura.'}
              </p>
              <Button 
                onClick={() => navigate('/nova-leitura')}
                className="bg-gradient-to-r from-[#2E5C55] to-[#3A7068] hover:from-[#234744] hover:to-[#2E5C55] text-white rounded-full px-8 h-12 font-semibold"
                data-testid="first-reading-btn"
              >
                Fazer primeira leitura
              </Button>
            </div>
          )}
        </section>
      </main>
      <FloatingNav />
    </div>
  );
};

export default HistoryPage;
